/**
 * Configure Book Custom Element
 * Handles configuration of a single book (variant selection, upsells, uploads)
 */
class ConfigureBook extends HTMLElement {
  constructor() {
    super();

    this.instanceId = this.generateUUID();
  }

  connectedCallback() {
    // Get configuration from data attributes
    this.themeKey = this.dataset.themeKey || "";
    this.themeTitle = this.dataset.themeTitle || "";
    this.blockId = this.dataset.blockId || "";
    this.groupId = this.dataset.groupId || this.generateUUID();
    this.themeImageSrc = this.dataset.themeImage || "";
    this.themeImageAlt = this.dataset.themeImageAlt || this.themeTitle;
    this.variantImageSrc = this.dataset.variantImage || "";
    this.variantImageAlt = this.dataset.variantImageAlt || this.themeTitle;
    this.variantSelectorStyle =
      this.dataset.variantSelectorStyle || "format-card";

    // Parse product variants
    try {
      this.variants = JSON.parse(this.dataset.variants || "[]");
      // Sort variants by price ascending
      this.variants.sort((a, b) => (a.price || 0) - (b.price || 0));
    } catch (e) {
      console.error("Error parsing variants:", e);
      this.variants = [];
    }

    // Parse upsell data
    this.dedicationPrice = parseInt(this.dataset.dedicationPrice || "0");
    this.photoPrice = parseInt(this.dataset.photoPrice || "0");
    this.dedicationVariantId = this.dataset.dedicationVariantId || "";
    this.photoVariantId = this.dataset.photoVariantId || "";
    this.dedicationImage = this.dataset.dedicationImage || "";
    this.photoImage = this.dataset.photoImage || "";
    this.dedicationImages = this.parseDedicationImages();
    this.showLanguageUpsell = this.parseBoolean(
      this.dataset.showLanguageUpsell,
      false,
    );

    // Currency / money formatting (accept from data attributes, fallback to theme settings)
    this.currencyIsoCode = this.dataset.currencyIsoCode || "";
    this.currencySymbol = this.dataset.currencySymbol || "";
    this.moneyFormat =
      this.dataset.moneyFormat || window.theme?.settings?.moneyFormat || "";
    this.moneyWithCurrencyFormat =
      this.dataset.moneyWithCurrencyFormat ||
      window.theme?.settings?.moneyWithCurrencyFormat ||
      "";
    this.currencyCodeEnabled = this.parseBoolean(
      this.dataset.currencyCodeEnabled,
      window.theme?.settings?.currencyCodeEnabled,
    );

    // Render the component
    this.render();

    // Set up event listeners
    this.setupEventListeners();

    // Initialize price display
    this.updateUpsellPriceVisibility();
    this.updateTotalPrice();
  }

  // Use global translations object
  get strings() {
    return theme.personalizedBookStrings || {};
  }

  render() {
    const s = this.strings;
    this.className = "pbk-configure-book";
    this.dataset.instanceId = this.instanceId;

    this.innerHTML = `
      <div class="pbk-configure-book-header">
        <div style="display: flex; align-items: center; gap: 12px; flex: 1;">
          ${
            this.themeImageSrc
              ? `<img src="${this.escapeHtml(
                  this.themeImageSrc,
                )}" alt="${this.escapeHtml(
                  this.themeImageAlt,
                )}" style="width: 60px; height: 60px; object-fit: cover; border-radius: 8px; flex-shrink: 0;">`
              : ""
          }
          <h3 class="pbk-configure-book-title">${this.escapeHtml(
            this.themeTitle,
          )}</h3>
        </div>
        <button type="button" class="pbk-configure-book-remove" data-remove-btn>
          ${s.remove || "Remove"}
        </button>
      </div>
      
      <!-- Variant selector -->
      <div class="field field--full">
        <div class="pbk-caption" style="margin-bottom: 8px; font-weight: 500; opacity: 0.9;">${s.format || "Format"}</div>
        ${
          this.variantImageSrc
            ? `<div class="pbk-variant-preview-image">
                <img src="${this.escapeHtml(
                  this.variantImageSrc,
                )}" alt="${this.escapeHtml(
                  this.variantImageAlt,
                )}" loading="lazy">
              </div>`
            : ""
        }
        <personalized-book-variant-selector
          data-instance-id="${this.escapeAttribute(this.instanceId)}"
          data-style="${this.escapeAttribute(this.variantSelectorStyle)}"
          data-label="${this.escapeAttribute(s.format || "Format")}"
          data-selected-text="${this.escapeAttribute(s.selected || "Selected")}"
          data-variants="${this.escapeAttribute(JSON.stringify(this.variants))}"
          data-money-format="${this.escapeAttribute(this.moneyFormat)}"
          data-money-with-currency-format="${this.escapeAttribute(
            this.moneyWithCurrencyFormat,
          )}"
          data-currency-code-enabled="${this.escapeAttribute(
            String(this.currencyCodeEnabled),
          )}"
          data-currency-iso-code="${this.escapeAttribute(this.currencyIsoCode)}"
          data-currency-symbol="${this.escapeAttribute(this.currencySymbol)}"
        ></personalized-book-variant-selector>
      </div>

      <!-- Upsells -->
      <div class="pbk-upsells">
        <div class="upsell-title">${s.extras || "Extras"}</div>

        ${
          this.showLanguageUpsell
            ? `
        <!-- Book language (PL storefront: optional English book) -->
        <label class="pbk-upsell-label" style="margin-top:8px">
          <div class="flex items-center gap-3">
            <input type="checkbox" class="pbk-upsell-language-toggle" data-book-language-english-toggle>
            <span>${s.bookInEnglish || "Book in English"}</span>
          </div>
          <span class="pbk-upsell-price hidden" aria-hidden="true"></span>
        </label>
        `
            : ""
        }

        <!-- Dedication upsell -->
        <label class="pbk-upsell-label" style="margin-top:8px">
          <div class="flex items-center gap-3">
            <input type="checkbox" class="pbk-upsell-dedication-toggle" data-dedication-toggle>
            ${
              this.dedicationImage
                ? `<img src="${this.escapeHtml(
                    this.dedicationImage,
                  )}" alt="${s.addDedicationPage || "Dedication"}" style="width: 40px; height: 40px; object-fit: cover; border-radius: 6px; flex-shrink: 0;">`
                : ""
            }
            <span>${s.addDedicationPage || "Add dedication page"}</span>
          </div>
          <span class="pbk-upsell-price" data-dedication-price>
            ${
              this.dedicationPrice > 0
                ? this.formatPrice(this.dedicationPrice)
                : ""
            }
          </span>
        </label>

        <div class="pbk-dedication-fields pbk-hidden" data-dedication-fields style="margin-top:10px">
          <div class="pbk-upsell-expanded-layout">
            <div class="pbk-upsell-form">
              ${
                this.dedicationImages.length > 1
                  ? `<div class="field field--full pbk-dedication-layout-field">
                      <div class="pbk-caption" style="margin-bottom: 8px; font-weight: 500; opacity: 0.9;">${s.dedicationLayoutLabel || "Dedication page design"}</div>
                      <div class="pbk-dedication-layout-row" role="radiogroup" aria-label="${s.dedicationLayoutLabel || "Dedication page design"}">
                        ${this.renderDedicationLayoutOptions()}
                      </div>
                    </div>`
                  : ""
              }
              <div class="field">
                <input class="input is-floating pbk-dedication-title" type="text" maxlength="50" placeholder=" " data-dedication-title />
                <label class="label is-floating">${s.dedicationTitleLabel || "Dedication title (e.g., For Eva) (max 50 chars - optional)"}</label>
              </div>
              <div class="field field--full">
                <textarea class="textarea is-floating pbk-dedication-message" rows="4" maxlength="1000" placeholder=" " data-dedication-message required></textarea>
                <label class="label is-floating">${s.dedicationMessageLabel || "Dedication message (max 1000 chars)"}</label>
              </div>
              <div class="field">
                <input class="input is-floating pbk-dedication-from" type="text" maxlength="50" placeholder=" " data-dedication-from required />
                <label class="label is-floating">${s.dedicationFromLabel || "Signature (max 50 chars)"}</label>
              </div>
            </div>
            ${
              this.dedicationImage
                ? `<div class="pbk-upsell-image">
                    <img src="${this.escapeHtml(
                      this.dedicationImage,
                    )}" alt="${s.addDedicationPage || "Dedication"}" data-dedication-preview />
                  </div>`
                : ""
            }
          </div>
        </div>

        <!-- Final photo upsell - only show if configured -->
        ${
          this.photoPrice > 0 && this.photoVariantId
            ? `
        <label class="pbk-upsell-label" style="margin-top:12px">
          <div class="flex items-center gap-3">
            <input type="checkbox" class="pbk-upsell-photo-toggle" data-photo-toggle>
            ${
              this.photoImage
                ? `<img src="${this.escapeHtml(
                    this.photoImage,
                  )}" alt="${s.addFinalPhotoPage || "Final photo page"}" style="width: 40px; height: 40px; object-fit: cover; border-radius: 6px; flex-shrink: 0;">`
                : ""
            }
            <span>${s.addFinalPhotoPage || "Add final page with photo"}</span>
          </div>
          <span class="pbk-upsell-price" data-photo-price>
            ${this.formatPrice(this.photoPrice)}
          </span>
        </label>

        <div class="pbk-photo-fields pbk-hidden" data-photo-fields style="margin-top:10px">
          <div class="pbk-upsell-expanded-layout">
            <div class="pbk-upsell-form">
              <div class="field field--full">
                <textarea class="textarea is-floating pbk-finalphoto-message" rows="2" maxlength="50" placeholder=" " data-photo-message required></textarea>
                <label class="label is-floating">${s.finalPhotoMessageLabel || "Message on final page (max 50)"}</label>
              </div>
              <div class="field field--full">
                <div class="pbk-finalphoto-upload">
                  <label for="pbk-finalphoto-file-${
                    this.instanceId
                  }" class="pbk-upload-label" data-upload-label>
                    <div class="pbk-upload-content">
                      <div class="pbk-upload-icon">📷</div>
                      <div class="pbk-upload-text" data-upload-text>${s.selectFinalPhoto || "Select final photo"}</div>
                      <div class="pbk-upload-caption">${s.uploadClickOrDrag || "Click to select file"}</div>
                      <div class="pbk-upload-format">${s.uploadFormat || "JPG, PNG (max 10MB)"}</div>
                    </div>
                    <input type="file" id="pbk-finalphoto-file-${
                      this.instanceId
                    }" class="pbk-upload-input" accept="image/*" style="display: none;" data-photo-file required>
                    <input type="hidden" class="pbk-finalphoto-url" data-photo-url>
                  </label>
                </div>
              </div>
            </div>
            ${
              this.photoImage
                ? `<div class="pbk-upsell-image">
                    <img src="${this.escapeHtml(
                      this.photoImage,
                    )}" alt="${s.addFinalPhotoPage || "Final photo page"}" />
                  </div>`
                : ""
            }
          </div>
        </div>
        `
            : ""
        }
        
        <!-- Total price display -->
        <div class="pbk-total-price pbk-hidden" data-total-price>
          <div class="total-label">${s.bookPrice || "Book price"}</div>
          <div class="total-amount" data-total-amount>${this.formatPrice(
            0,
          )}</div>
        </div>
      </div>
    `;
  }

  renderDedicationLayoutOptions() {
    return this.dedicationImages
      .map((image, index) => {
        const inputId = `pbk-dedication-layout-${this.instanceId}-${index}`;
        const isChecked = image.url === this.dedicationImage || index === 0;
        const altText = image.alt || `Dedication layout ${index + 1}`;

        return `
          <input
            type="radio"
            name="pbk-dedication-layout-${this.instanceId}"
            id="${inputId}"
            value="${this.escapeHtml(image.url)}"
            ${isChecked ? "checked" : ""}
            data-dedication-layout>
          <label for="${inputId}" aria-label="${this.escapeHtml(altText)}">
            <img
              src="${this.escapeHtml(image.url)}"
              alt="${this.escapeHtml(altText)}"
              class="pbk-dedication-layout-thumb"
              loading="lazy">
          </label>
        `;
      })
      .join("");
  }

  setupEventListeners() {
    // Remove button
    const removeBtn = this.querySelector("[data-remove-btn]");
    removeBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.handleRemove();
    });

    // Dedication toggle
    const dedToggle = this.querySelector("[data-dedication-toggle]");
    const dedFields = this.querySelector("[data-dedication-fields]");
    dedToggle?.addEventListener("change", () => {
      dedFields?.classList.toggle("pbk-hidden", !dedToggle.checked);
      this.updateDedicationPreview();
      this.updateTotalPrice();
      this.notifyChange();
    });

    // Photo toggle
    const photoToggle = this.querySelector("[data-photo-toggle]");
    const photoFields = this.querySelector("[data-photo-fields]");
    photoToggle?.addEventListener("change", () => {
      photoFields?.classList.toggle("pbk-hidden", !photoToggle.checked);
      this.updateTotalPrice();
      this.notifyChange();
    });

    // Book in English (optional; only rendered when showLanguageUpsell is set)
    const languageToggle = this.querySelector(
      "[data-book-language-english-toggle]",
    );
    languageToggle?.addEventListener("change", () => {
      this.notifyChange();
    });

    // Variant selector radios are rendered by a child custom element.
    this.addEventListener("change", (event) => {
      if (event.target?.matches?.("[data-variant-radio]")) {
        this.updateUpsellPriceVisibility();
        this.notifyChange();
      }
    });

    const dedicationLayoutRadios = this.querySelectorAll(
      "[data-dedication-layout]",
    );
    dedicationLayoutRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        this.updateDedicationPreview();
        this.notifyChange();
      });
    });

    // Photo file upload - using same S3 upload as character-form
    const photoFileInput = this.querySelector("[data-photo-file]");
    const photoUrlInput = this.querySelector("[data-photo-url]");
    const uploadLabel = this.querySelector("[data-upload-label]");
    const uploadText = this.querySelector("[data-upload-text]");

    photoFileInput?.addEventListener("change", async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        // Show loading state
        if (uploadText) {
          uploadText.textContent =
            this.strings.uploadUploading || "Uploading...";
        }
        uploadLabel?.classList.add("pbk-upload-loading");
        uploadLabel?.classList.remove(
          "pbk-upload-error",
          "pbk-upload-selected",
        );

        // Step 1: Get presigned URL from API
        const response = await window.ChildbookStorefrontSession.fetch(
          "https://www.childbook.ai/api/upload-child-photo",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              fileName: file.name,
              contentType: file.type,
            }),
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to get upload URL: ${response.statusText}`);
        }

        const { putUrl, key } = await response.json();

        // Step 2: Upload file to S3 using presigned URL
        const uploadResponse = await fetch(putUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Failed to upload file: ${uploadResponse.statusText}`,
          );
        }

        //Build the s3 key
        //`s3://childbook-photo-uploads/${key}`
        // const s3Key = `s3://childbook-photo-uploads/${key}`;
        // const base64S3Key = this.base64Encode(s3Key);

        // Step 3: The previewable URL: `https://images.childbook.ai/sig/${base64S3Key}`;
        photoUrlInput.value = key;

        // Update UI to show success with image preview
        const reader = new FileReader();
        const changePhotoText =
          this.strings.uploadChangePhoto || "Change photo";
        reader.onload = (e) => {
          if (uploadLabel) {
            const uploadContent = uploadLabel.querySelector(
              ".pbk-upload-content",
            );
            if (uploadContent) {
              uploadContent.innerHTML = `
                <div class="pbk-upload-preview">
                  <img src="${e.target.result}" alt="Preview" class="pbk-upload-preview-image">
                  <div class="pbk-upload-preview-name">${file.name}</div>
                  <div class="pbk-upload-change-btn">
                    <span>🔄</span>
                    <span>${changePhotoText}</span>
                  </div>
                </div>
              `;
            }
          }
        };
        reader.readAsDataURL(file);

        uploadLabel?.classList.remove("pbk-upload-loading");
        uploadLabel?.classList.add("pbk-upload-selected");

        // Dispatch custom event to notify parent
        this.dispatchEvent(
          new CustomEvent("configure-book:photo-uploaded", {
            bubbles: true,
            composed: true,
            detail: { url: key, key, file, instanceId: this.instanceId },
          }),
        );
      } catch (error) {
        console.error("Error uploading final photo:", error);

        // Show error state
        const uploadContent = uploadLabel?.querySelector(".pbk-upload-content");
        const s = this.strings;
        if (uploadContent) {
          uploadContent.innerHTML = `
            <div class="pbk-upload-icon">⚠️</div>
            <div class="pbk-upload-text">${s.uploadError || "Upload error"}</div>
            <div class="pbk-upload-caption">${s.uploadRetry || "Click to try again"}</div>
            <div class="pbk-upload-format">${s.uploadFormat || "JPG, PNG (max 10MB)"}</div>
          `;
        }
        uploadLabel?.classList.remove("pbk-upload-loading");
        uploadLabel?.classList.add("pbk-upload-error");

        // Clear the file input so user can retry
        photoFileInput.value = "";

        // Dispatch error event
        this.dispatchEvent(
          new CustomEvent("configure-book:photo-error", {
            bubbles: true,
            composed: true,
            detail: { error: error.message, file, instanceId: this.instanceId },
          }),
        );
      }
    });
  }

  handleRemove() {
    // Dispatch custom event to notify parent
    this.dispatchEvent(
      new CustomEvent("configure-book:remove", {
        detail: {
          themeKey: this.themeKey,
          instanceId: this.instanceId,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  notifyChange() {
    // Dispatch custom event when configuration changes
    this.dispatchEvent(
      new CustomEvent("configure-book:change", {
        detail: {
          themeKey: this.themeKey,
          instanceId: this.instanceId,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  updateUpsellPriceVisibility() {
    const selectedVariant = this.querySelector("[data-variant-radio]:checked");
    if (!selectedVariant || this.variants.length === 0) return;

    const selectedVariantId = selectedVariant.value;
    const variant = this.variants.find(
      (v) => v.id.toString() === selectedVariantId,
    );
    const currentMainPrice = variant ? variant.price : this.variants[0].price;

    // Check dedication price
    const dedicationPriceEl = this.querySelector("[data-dedication-price]");
    if (dedicationPriceEl && this.dedicationPrice > 0) {
      if (this.dedicationPrice === currentMainPrice) {
        dedicationPriceEl.classList.add("hidden");
      } else {
        dedicationPriceEl.classList.remove("hidden");
      }
    }

    // Check photo price (only if photo upsell is configured)
    const photoPriceEl = this.querySelector("[data-photo-price]");
    if (photoPriceEl && this.photoPrice > 0 && this.photoVariantId) {
      if (this.photoPrice === currentMainPrice) {
        photoPriceEl.classList.add("hidden");
      } else {
        photoPriceEl.classList.remove("hidden");
      }
    }

    // Update total price
    this.updateTotalPrice();
  }

  updateTotalPrice() {
    const selectedVariant = this.querySelector("[data-variant-radio]:checked");
    if (!selectedVariant || this.variants.length === 0) return;

    const selectedVariantId = selectedVariant.value;
    const variant = this.variants.find(
      (v) => v.id.toString() === selectedVariantId,
    );
    const currentMainPrice = variant ? variant.price : this.variants[0].price;

    let totalPrice = currentMainPrice;

    // Add dedication price if selected
    const dedicationToggle = this.querySelector("[data-dedication-toggle]");
    if (
      dedicationToggle &&
      dedicationToggle.checked &&
      this.dedicationPrice > 0
    ) {
      totalPrice += this.dedicationPrice;
    }

    // Add photo price if selected (only if photo upsell is configured)
    const photoToggle = this.querySelector("[data-photo-toggle]");
    if (
      photoToggle &&
      photoToggle.checked &&
      this.photoPrice > 0 &&
      this.photoVariantId
    ) {
      totalPrice += this.photoPrice;
    }

    // Update total price display
    const totalPriceEl = this.querySelector("[data-total-price]");
    const totalAmountEl = this.querySelector("[data-total-amount]");
    if (totalPriceEl && totalAmountEl) {
      totalAmountEl.textContent = this.formatPrice(totalPrice);
      totalPriceEl.classList.remove("pbk-hidden");
    }
  }

  updateDedicationPreview() {
    const preview = this.querySelector("[data-dedication-preview]");
    const selectedLayout = this.getSelectedDedicationLayout();

    if (preview && selectedLayout?.url) {
      preview.src = selectedLayout.url;
      preview.alt =
        selectedLayout.alt || this.strings.addDedicationPage || "Dedication";
    }
  }

  // Public API - Get configuration data
  getConfiguration() {
    const selectedVariant = this.querySelector("[data-variant-radio]:checked");
    const variantId = selectedVariant
      ? selectedVariant.value
      : this.variants[0]?.id || "";

    const dedicationToggle = this.querySelector("[data-dedication-toggle]");
    const photoToggle = this.querySelector("[data-photo-toggle]");
    const bookLanguageToggle = this.querySelector(
      "[data-book-language-english-toggle]",
    );
    const selectedDedicationLayout = this.getSelectedDedicationLayout();

    return {
      themeKey: this.themeKey,
      themeTitle: this.themeTitle,
      themeImage: this.themeImageSrc,
      blockId: this.blockId,
      groupId: this.groupId,
      instanceId: this.instanceId,
      variantId: variantId,
      bookInEnglish:
        this.showLanguageUpsell && (bookLanguageToggle?.checked || false),
      dedication: {
        enabled: dedicationToggle?.checked || false,
        title: this.querySelector("[data-dedication-title]")?.value || "",
        message: this.querySelector("[data-dedication-message]")?.value || "",
        from: this.querySelector("[data-dedication-from]")?.value || "",
        layoutImageUrl: selectedDedicationLayout?.url || "",
      },
      finalPhoto: {
        enabled: photoToggle?.checked || false,
        message: this.querySelector("[data-photo-message]")?.value || "",
        url: this.querySelector("[data-photo-url]")?.value || "",
      },
    };
  }

  // Utility methods
  formatPrice(priceInCents) {
    const cents =
      typeof priceInCents === "number"
        ? priceInCents
        : parseInt(priceInCents || "0", 10);

    const format =
      this.currencyCodeEnabled && this.moneyWithCurrencyFormat
        ? this.moneyWithCurrencyFormat
        : this.moneyFormat;

    // Prefer theme's money formatter (matches Shopify money formats)
    if (window.theme?.Currency?.formatMoney && format) {
      return window.theme.Currency.formatMoney(cents, format);
    }

    // Fallback: Intl if we have currency code, else basic amount + symbol
    const locale =
      document.documentElement.getAttribute("lang") ||
      navigator.language ||
      "en";

    if (this.currencyIsoCode) {
      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: this.currencyIsoCode,
        }).format(cents / 100);
      } catch (e) {
        // ignore and fallback below
      }
    }

    const amount = (cents / 100).toFixed(2);
    const symbol =
      this.currencySymbol || (theme.settings?.locale === "en" ? "£" : "zł");
    return `${amount} ${symbol}`;
  }

  generateUUID() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  parseBoolean(value, fallback = false) {
    if (value === undefined || value === null || value === "") {
      return !!fallback;
    }
    if (typeof value === "boolean") return value;
    const normalized = String(value).trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }

  parseDedicationImages() {
    let parsedImages = [];

    try {
      parsedImages = JSON.parse(this.dataset.dedicationImages || "[]");
    } catch (error) {
      console.error("Error parsing dedication images:", error);
    }

    const images = [];
    const seenUrls = new Set();

    const addImage = (image) => {
      if (!image?.url || seenUrls.has(image.url)) return;

      images.push({
        url: image.url,
        alt: image.alt || this.strings.addDedicationPage || "Dedication",
      });
      seenUrls.add(image.url);
    };

    addImage({
      url: this.dedicationImage,
      alt: this.strings.addDedicationPage || "Dedication",
    });

    parsedImages.forEach((image) => addImage(image));

    if (!this.dedicationImage && images[0]?.url) {
      this.dedicationImage = images[0].url;
    }

    return images;
  }

  getSelectedDedicationLayout() {
    const selectedLayoutInput = this.querySelector(
      "[data-dedication-layout]:checked",
    );
    const selectedUrl =
      selectedLayoutInput?.value || this.dedicationImage || "";

    return (
      this.dedicationImages.find((image) => image.url === selectedUrl) || {
        url: selectedUrl,
        alt: this.strings.addDedicationPage || "Dedication",
      }
    );
  }

  escapeHtml(text) {
    if (!text) return "";
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  escapeAttribute(text) {
    return this.escapeHtml(text).replace(/"/g, "&quot;");
  }

  base64Encode(text) {
    try {
      const path = text.split("/");
      const fileName = path.pop();
      if (!fileName) {
        return text;
      }
      let safeKey = `${path.join("/")}/${encodeURIComponent(fileName)}`;

      safeKey = safeKey.replaceAll("%20", " ");
      return btoa(safeKey);
    } catch (error) {
      return text;
    }
  }
}

// Register the custom element
customElements.define("configure-book", ConfigureBook);
